import React, {forwardRef, useEffect, useRef} from 'react';

import {noop} from '@pexip/utils';

export const Audio: React.FC<
    React.ComponentProps<'audio'> & {
        src?: string;
        srcObject?: MediaStream;
        play?: boolean;
        controls?: boolean;
        captionsSrc?: string;
        captionsSrcLang?: string;
        onFailure?: (reason: unknown) => void;
        onSuccess?: (audio: HTMLAudioElement | null) => void;
        sinkId?: string;
    }
> = forwardRef(
    (
        {
            src,
            srcObject,
            captionsSrc,
            captionsSrcLang,
            sinkId,
            play,
            controls = false,
            onFailure = noop,
            onSuccess = noop,
            ...props
        },
        ref,
    ) => {
        const audioRef = useRef<HTMLAudioElement>(null);

        useEffect(() => {
            audioRef.current?.setSinkId?.(sinkId ?? '').catch(onFailure);
        }, [sinkId, onFailure]);

        useEffect(() => {
            if (audioRef.current && srcObject) {
                audioRef.current.srcObject = srcObject;
                audioRef.current.setSinkId?.(sinkId ?? '').catch(onFailure);
            }
        }, [sinkId, srcObject, onFailure]);

        useEffect(() => {
            const playAudio = async (play: boolean) => {
                try {
                    if (play) {
                        await audioRef.current?.play();
                        audioRef.current && onSuccess(audioRef.current);
                    } else {
                        audioRef.current?.pause();
                    }
                } catch (error) {
                    onFailure(error);
                }
            };
            if (play !== undefined) {
                void playAudio(play);
            }
        }, [onFailure, onSuccess, play]);

        return (
            <audio
                ref={el => {
                    if (ref) {
                        if ('current' in ref) {
                            ref.current = el;
                        } else {
                            ref(el);
                        }
                    }
                    audioRef.current = el;
                }}
                src={src}
                controls={controls}
                {...props}
            >
                {captionsSrc && (
                    <track
                        kind="captions"
                        src={captionsSrc}
                        srcLang={captionsSrcLang}
                    />
                )}
            </audio>
        );
    },
);
